/**
 * Laravel Echo (Reverb / WebSocket) : temps réel widget + Inbox.
 * - Sans token : canaux publics (ex. conversation.{id} pour le widget).
 * - Avec token : canaux privés (ex. organization.{id} pour l'Inbox).
 */

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

window.Pusher = Pusher;

const key = import.meta.env.VITE_REVERB_APP_KEY as string | undefined;
const host = import.meta.env.VITE_REVERB_HOST as string | undefined;
const port = import.meta.env.VITE_REVERB_PORT as string | undefined;
const scheme = (import.meta.env.VITE_REVERB_SCHEME as string) || 'http';
const apiBase = (import.meta.env.VITE_API_URL as string) || 'http://localhost:8000/api';

let echoInstancePublic: Echo<'reverb'> | null = null;
let echoInstanceAuthed: Echo<'reverb'> | null = null;
let echoAuthedToken: string | null = null;

const baseConfig = {
  broadcaster: 'reverb' as const,
  key: key!,
  wsHost: host || 'localhost',
  wsPort: port ? Number(port) : 8080,
  wssPort: port ? Number(port) : 8080,
  forceTLS: scheme === 'https',
  enabledTransports: ['ws', 'wss'] as ('ws' | 'wss')[],
};

/**
 * Retourne l'instance Echo. Avec authToken : canaux privés (Inbox). Sans : canaux publics (widget).
 */
export function getEcho(authToken?: string | null): Echo<'reverb'> | null {
  if (!key) {
    console.warn('[echo] VITE_REVERB_APP_KEY manquant : temps réel désactivé (fallback silencieux sur getEcho() === null).');
    return null;
  }

  if (authToken) {
    if (echoInstanceAuthed && echoAuthedToken === authToken) return echoInstanceAuthed;
    echoInstanceAuthed = new Echo({
      ...baseConfig,
      authEndpoint: `${apiBase.replace(/\/$/, '')}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: 'application/json',
        },
      },
    });
    echoAuthedToken = authToken;
    return echoInstanceAuthed;
  }

  if (echoInstancePublic) return echoInstancePublic;
  echoInstancePublic = new Echo(baseConfig);
  return echoInstancePublic;
}

/** Payload reçu sur .message.sent (broadcast Laravel) */
export interface MessageSentPayload {
  message: {
    id: number;
    conversation_id?: number;
    sender_id?: number;
    content: string;
    sender_type: string;
    created_at: string;
    conversation?: { id: number; site_id?: number; visitor_id?: number; status?: string };
    sender?: { id: number; name: string; avatar?: string | null };
    reply_to?: { id: number; content: string; sender_type: string; sender_name?: string | null } | null;
  };
  conversation_id?: string;
  /** Nombre de messages visiteur non lus (source de vérité pour le badge). */
  unread_count?: number;
}

/** Payload reçu sur .message.updated (édition d'un message, une seule fois par message) */
export interface MessageUpdatedPayload {
  message_id: number;
  conversation_id: string;
  content: string;
  original_content: string | null;
  edited_at: string;
}

/** Payload reçu sur .conversation.created */
export interface ConversationCreatedPayload {
  conversation: {
    id: number;
    organization_id: string;
    site_id: string;
    visitor_id: string;
    status: string;
    assigned_agent_id: string | null;
    created_at: string;
    closed_at: string | null;
  };
  visitor: { first_name?: string | null; email?: string | null };
  last_message: null;
  unread_count: number;
}

/** Payload reçu sur .conversation.updated */
export interface ConversationUpdatedPayload {
  conversation_id: string;
  status: string | null;
  assigned_agent_id: string | null;
  closed_at: string | null;
}

/** Payload reçu sur .conversation.deleted */
export interface ConversationDeletedPayload {
  conversation_id: string;
}

/** Payload reçu sur .conversation.read (sync badge non lu pour tous les agents) */
export interface ConversationReadPayload {
  conversation_id: string;
  unread_count: number;
}

/** Payload reçu sur .message.read (accusé de lecture visiteur sur les messages agent) */
export interface MessageReadPayload {
  conversation_id: string;
  read_at: string;
}

/** Payload reçu sur .visitor.status.updated */
export interface VisitorStatusUpdatedPayload {
  visitor_id: string;
  status: 'active' | 'left';
}

/** Payload reçu sur .visitor.typing (indicateur "en train d'écrire") */
export interface VisitorTypingPayload {
  conversation_id: string;
  status: 'typing' | 'stopped';
}
